import { useState, useCallback, useEffect } from 'react';
import { Box, Button, CircularProgress, Snackbar, Alert, Stepper, Step, StepLabel, Typography } from '@mui/material';
import { useDataProvider } from 'react-admin';

interface OHSWorkflowStatusProps {
  entityId: string;
  entityType: 'first-aider' | 'certification' | 'representative' | 'injury' | 'calendar-event';
  currentState?: string;
  onSuccess?: () => void;
}

const STATUS_STEPS: Record<string, string[]> = {
  'first-aider': ['Draft', 'Active', 'Suspended', 'Expired'],
  'certification': ['Draft', 'Active', 'Suspended', 'Expired'],
  'representative': ['Draft', 'Active', 'Suspended', 'Expired'],
  'injury': ['Open', 'Investigating', 'Resolved', 'Closed'],
  'calendar-event': ['Scheduled', 'InProgress', 'Completed', 'Cancelled'],
};

const TRANSITIONS: Record<string, Record<string, { event: string; label: string }[]>> = {
  'first-aider': {
    'Draft': [{ event: 'activate', label: 'Activate' }],
    'Active': [
      { event: 'suspend', label: 'Suspend' },
      { event: 'expire', label: 'Expire' },
    ],
    'Suspended': [
      { event: 'activate', label: 'Reactivate' },
      { event: 'expire', label: 'Expire' },
    ],
  },
  'certification': {
    'Draft': [{ event: 'activate', label: 'Activate' }],
    'Active': [
      { event: 'suspend', label: 'Suspend' },
      { event: 'renew', label: 'Renew' },
      { event: 'expire', label: 'Expire' },
    ],
    'Suspended': [
      { event: 'activate', label: 'Reactivate' },
      { event: 'expire', label: 'Expire' },
    ],
  },
  'representative': {
    'Draft': [{ event: 'activate', label: 'Activate' }],
    'Active': [
      { event: 'suspend', label: 'Suspend' },
      { event: 'endTerm', label: 'End Term' },
    ],
    'Suspended': [
      { event: 'activate', label: 'Reactivate' },
      { event: 'endTerm', label: 'End Term' },
    ],
  },
  'injury': {
    'Open': [{ event: 'investigate', label: 'Start Investigation' }],
    'Investigating': [
      { event: 'resolve', label: 'Resolve' },
    ],
    'Resolved': [{ event: 'close', label: 'Close' }],
  },
  'calendar-event': {
    'Scheduled': [{ event: 'start', label: 'Start' }],
    'InProgress': [{ event: 'complete', label: 'Complete' }],
  },
};

export const OHSWorkflowStatus: React.FC<OHSWorkflowStatusProps> = ({
  entityId,
  entityType,
  currentState: initialStatus,
  onSuccess,
}) => {
  const dataProvider = useDataProvider();
  const [currentState, setCurrentState] = useState(initialStatus || STATUS_STEPS[entityType]?.[0] || 'Draft');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const steps = STATUS_STEPS[entityType] || [];
  const activeStep = steps.indexOf(currentState);
  const availableTransitions = TRANSITIONS[entityType]?.[currentState] || [];

  const handleTransition = useCallback(async (event: string) => {
    setLoading(true);
    try {
      // Find workflow instance for this entity
      const { data: workflows } = await dataProvider.getList('workflow-instances', {
        filter: { entityType, entityId },
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'createdAt', order: 'DESC' },
      });

      if (workflows && workflows.length > 0) {
        const workflow = workflows[0];
        const response = await fetch(`/api/v1/workflow-instances/${workflow.id}/transition`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Transition failed');
        }

        // Update local state based on transition
        const nextStepIndex = steps.indexOf(currentState) + 1;
        if (nextStepIndex < steps.length) {
          setCurrentState(steps[nextStepIndex]);
        }
      } else {
        // No workflow instance - just update local state for demo
        const nextStepIndex = steps.indexOf(currentState) + 1;
        if (nextStepIndex < steps.length) {
          setCurrentState(steps[nextStepIndex]);
        }
      }

      setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Transition failed';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [dataProvider, entityType, entityId, currentState, steps, onSuccess]);

  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Workflow Status</Typography>
      <Stepper activeStep={activeStep >= 0 ? activeStep : 0} alternativeLabel sx={{ mb: 2 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {availableTransitions.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {availableTransitions.map((t) => (
            <Button
              key={t.event}
              variant="outlined"
              size="small"
              startIcon={loading ? <CircularProgress size={14} /> : undefined}
              onClick={() => handleTransition(t.event)}
              disabled={loading}
            >
              {t.label}
            </Button>
          ))}
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OHSWorkflowStatus;
